import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RefreshCcw, Phone, Mail, MessageCircle, Calendar,
  Clock, Activity, CheckCircle, XCircle, AlertCircle, Trash2,
} from 'lucide-react';
import TableColumnHeaderFilter from '../../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../../core/services/localApiAdapter';
import CustomSelect from '../../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../../shared/components/custom/CustomDatePicker';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../../products/saudi/components/QawaemErpCrudModal';
import {
  CrmModalFormTabs,
  CrmModalFormBody,
  CrmModalFormPanel,
  CrmModalFieldGrid,
} from './CrmModalFormTabs';
import QawaemClickableStatusBadge from '../../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../../products/saudi/components/QawaemTableHeaderActionsCell';
import { partitionStatusFromColumns } from '../../../shared/utils/erpListColumnOrder';
import { enrichStatusOptions } from '../../../shared/utils/crmPageUtils';
import useModuleConfigTheme from '../../../shared/hooks/useModuleConfigTheme';
import { exportToCSV } from '../../../shared/utils/importExportUtils';
import { useSaudiListDateRange } from '../../../products/saudi/hooks/useSaudiListDateRange';
import { useCrmConfig } from '../useCrmConfig';
import { resolveCrmListViewMode } from '../crmConfigDefaults';
import { filterCrmRecordsByDateRange, sliceCrmListItems } from '../crmListPageUtils';
import { useCrmContactsPicker, useCrmSalesAgentsPicker } from '../crmPickerOptions';
import { dispatchCrmApiRefresh, CRM_INTERACTIONS_REFRESH_EVENT, CRM_CONTACTS_REFRESH_EVENT } from '../crmRefreshEvents';
import { useCrmApiRefreshListener } from '../crmPickerOptions';
import {
  formatCrmDate,
  formatCrmMoney,
  formatCrmPhone,
  getCrmPhoneTelHref,
  getCrmWhatsAppHref,
  isSaudiWeekend,
} from '../utils/crmSaudiFormat';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../../products/saudi/components/QawaemTableSelection';
import { QawaemTableStickyActionsTd } from '../../../products/saudi/components/QawaemTableStickyActions';
import { AppNotificationToast, useAppNotificationToast } from '../../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../../shared/utils/crudNotify';

const FORM_ID = 'crm-interactions-form';

function normalizeInteraction(row, contactPhoneMap = {}) {
  const contactId = row.contact_id ?? null;
  return {
    id: row.id,
    contact_id: contactId,
    ref: row.interaction_ref || row.activity_ref || `INT-${row.id}`,
    contact: row.contact_name || row.customer_name || '—',
    phone: row.phone || row.contact_phone || contactPhoneMap[contactId] || '',
    type: row.interaction_type || row.activity_type || 'call',
    meetingType: row.meeting_type,
    subject: row.subject || row.activity_title || '—',
    date: row.interaction_date || row.activity_date,
    duration: row.duration_minutes ?? row.duration ?? 0,
    outcome: row.outcome || 'neutral',
    status: row.status || 'completed',
    dealAmount: row.deal_amount ?? row.deal_value ?? null,
    notes: row.notes || row.description || '',
    description: row.description || row.notes || '',
    nextAction: row.next_action,
    nextActionDate: row.next_action_date,
    assignedTo: row.assigned_to,
  };
}

const emptyForm = (typeFilter) => ({
  contact_id: '',
  interaction_type: typeFilter || 'call',
  subject: '',
  interaction_date: '',
  duration_minutes: '',
  outcome: 'neutral',
  status: 'completed',
  notes: '',
  description: '',
  next_action: '',
  next_action_date: '',
  assigned_to: '',
});

function TypeIcon({ type, className = 'h-4 w-4' }) {
  switch (type) {
    case 'email': return <Mail className={className} />;
    case 'whatsapp': return <MessageCircle className={className} />;
    case 'meeting': return <Calendar className={className} />;
    case 'sms': return <MessageCircle className={className} />;
    default: return <Phone className={className} />;
  }
}

/**
 * Shared CRM list for interactions API (/crm/interactions).
 * Used by InteractionsPage, MeetingsPage, CallsPage, EmailsPage.
 */
export default function CrmInteractionsListPage({
  pageKey,
  typeFilter = null,
  showPhone = false,
  showDealAmount = false,
  showMeetingType = false,
  showFollowUp = false,
  notesOnly = false,
}) {
  const { t, i18n } = useTranslation();
  const cfgTheme = useModuleConfigTheme();
  const crmConfig = useCrmConfig();
  const { dateRange } = useSaudiListDateRange();
  const p = (key, opts) => t(`modules.crm.pages.${pageKey}.${key}`, opts);
  const ps = (key, opts) => t(`modules.crm.pages.shared.${key}`, opts);
  const exportRef = useRef(null);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: p('new_interaction', { defaultValue: 'تفاعل' }) });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: p('title') });

  const [records, setRecords] = useState([]);
  const { contactOptions, contactPhoneMap } = useCrmContactsPicker();
  const { agentOptions } = useCrmSalesAgentsPicker();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [exportOpen, setExportOpen] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => emptyForm(typeFilter));
  const [formTab, setFormTab] = useState('general');
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => {
    setViewMode(resolveCrmListViewMode(crmConfig.defaultView));
  }, [crmConfig.defaultView]);

  const tableColumnDefs = useMemo(() => {
    const cols = [
      { key: 'ref', labelKey: 'col_ref' },
      { key: 'subject', labelKey: notesOnly ? 'col_note' : 'col_subject' },
      { key: 'contact', labelKey: 'col_contact' },
    ];
    if (showPhone) cols.push({ key: 'phone', labelKey: 'col_phone' });
    else cols.push({ key: 'type', labelKey: 'col_channel' });
    if (showMeetingType) cols.push({ key: 'meetingType', labelKey: 'col_type' });
    cols.push({ key: 'date', labelKey: 'col_date' });
    cols.push({ key: showPhone ? 'outcome' : 'duration', labelKey: showPhone ? 'col_outcome' : 'col_duration' });
    if (showDealAmount) cols.push({ key: 'dealAmount', labelKey: 'col_deal_value' });
    if (showFollowUp) cols.push({ key: 'nextActionDate', labelKey: 'col_follow_up' });
    cols.push({ key: 'status', labelKey: 'col_status', type: 'status' });
    return cols;
  }, [showPhone, showMeetingType, showDealAmount, showFollowUp, notesOnly]);

  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(tableColumnDefs.map((c) => [c.key, true]))
  );

  useEffect(() => {
    setColumnVisibility(Object.fromEntries(tableColumnDefs.map((c) => [c.key, true])));
  }, [tableColumnDefs]);

  const typeLabel = (type) => {
    const map = {
      call: ps('type_call'),
      email: ps('type_email'),
      meeting: ps('type_meeting'),
      whatsapp: ps('type_whatsapp'),
      sms: p('channel_sms') || 'رسائل نصية',
      task: ps('type_task'),
    };
    return map[type] || type;
  };

  const statusLabel = (s) => ({
    completed: ps('status_completed'),
    scheduled: ps('status_scheduled'),
    pending: ps('status_pending'),
    cancelled: ps('status_cancelled'),
  }[s] || s);

  const outcomeLabel = (o) => ({
    positive: ps('outcome_positive'),
    neutral: ps('outcome_neutral'),
    negative: ps('outcome_negative'),
  }[o] || o);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.interaction_type = typeFilter;
      const qs = new URLSearchParams(params).toString();
      const response = await localApiAdapter.get(`/crm/interactions${qs ? `?${qs}` : ''}`);
      let data = response?.data || response || [];
      if (!Array.isArray(data)) data = [];
      setRecords(data.map((row) => normalizeInteraction(row, contactPhoneMap)));
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, contactPhoneMap]);

  useEffect(() => { void loadRecords(); }, [loadRecords]);
  useCrmApiRefreshListener([CRM_INTERACTIONS_REFRESH_EVENT, CRM_CONTACTS_REFRESH_EVENT], loadRecords);

  const baseFiltered = useMemo(() => {
    let list = filterCrmRecordsByDateRange(records, dateRange, ['interaction_date', 'activity_date', 'date']);
    if (notesOnly) list = list.filter((r) => r.notes?.trim());
    return list;
  }, [records, notesOnly, dateRange]);

  const searchFiltered = useMemo(() => {
    if (viewMode !== 'cards' || !searchTerm) return baseFiltered;
    const q = searchTerm.toLowerCase();
    return baseFiltered.filter(
      (r) =>
        r.subject?.toLowerCase().includes(q) ||
        r.contact?.toLowerCase().includes(q) ||
        r.ref?.toLowerCase().includes(q) ||
        r.phone?.includes(q) ||
        r.notes?.toLowerCase().includes(q)
    );
  }, [baseFiltered, searchTerm, viewMode]);

  const statCards = useMemo(() => sliceQawaemStats([
    { key: 'total', label: p('stat_total'), value: searchFiltered.length, icon: Activity },
    ...(typeFilter === 'call' ? [] : [{ key: 'calls', label: p('stat_calls'), value: searchFiltered.filter((r) => r.type === 'call').length, icon: Phone, variant: 'forest' }]),
    ...(typeFilter === 'email' ? [] : [{ key: 'emails', label: p('stat_emails'), value: searchFiltered.filter((r) => r.type === 'email').length, icon: Mail }]),
    ...(typeFilter === 'meeting' ? [] : [{ key: 'meetings', label: p('stat_meetings'), value: searchFiltered.filter((r) => r.type === 'meeting').length, icon: Calendar, variant: 'forest' }]),
    { key: 'completed', label: ps('status_completed'), value: searchFiltered.filter((r) => r.status === 'completed').length, icon: CheckCircle },
    { key: 'scheduled', label: ps('status_scheduled'), value: searchFiltered.filter((r) => r.status === 'scheduled').length, icon: Clock },
  ], 4), [searchFiltered, typeFilter, p, ps]);

  const interactionStatusOptions = useMemo(() => enrichStatusOptions([
    { value: 'completed', label: ps('status_completed'), icon: CheckCircle },
    { value: 'scheduled', label: ps('status_scheduled'), icon: Clock },
    { value: 'pending', label: ps('status_pending'), icon: AlertCircle },
    { value: 'cancelled', label: ps('status_cancelled'), icon: XCircle },
  ]), [ps]);

  const interactionStatusFilterOptions = useMemo(
    () => interactionStatusOptions.map((opt) => ({ value: opt.value, label: opt.label })),
    [interactionStatusOptions],
  );

  const channelFilterOptions = useMemo(() => [
    { value: 'call', label: ps('type_call') },
    { value: 'email', label: ps('type_email') },
    { value: 'meeting', label: ps('type_meeting') },
    { value: 'whatsapp', label: ps('type_whatsapp') },
    { value: 'sms', label: p('channel_sms') || 'رسائل نصية' },
  ], [p, ps]);

  const outcomeFilterOptions = useMemo(() => [
    { value: 'positive', label: ps('outcome_positive') },
    { value: 'neutral', label: ps('outcome_neutral') },
    { value: 'negative', label: ps('outcome_negative') },
  ], [ps]);

  const typeFormOptions = useMemo(() => channelFilterOptions, [channelFilterOptions]);

  const { dataColumns, statusColumn } = useMemo(
    () => partitionStatusFromColumns(tableColumnDefs),
    [tableColumnDefs],
  );

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/crm/interactions/${recordId}`, { status: newStatus });
      dispatchCrmApiRefresh('/crm/interactions');
      loadRecords();
    } catch {
      notifyError(showNotify, ps('save_error') || t('common.save_error', { defaultValue: 'تعذر حفظ التغيير' }));
    }
  };

  const qawaemColumnDefs = useMemo(() => {
    const defs = dataColumns.map((col) => ({
      key: col.key,
      label: p(col.labelKey) || col.labelKey,
      checked: columnVisibility[col.key] !== false,
    }));
    if (statusColumn) {
      defs.push({
        key: statusColumn.key,
        label: p(statusColumn.labelKey) || statusColumn.labelKey,
        checked: columnVisibility[statusColumn.key] !== false,
      });
    }
    return defs;
  }, [dataColumns, statusColumn, columnVisibility, p]);

  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;

  const visibleDataColumns = useMemo(
    () => dataColumns.filter((c) => columnVisibility[c.key] !== false),
    [dataColumns, columnVisibility],
  );

  const showStatusColumn = statusColumn && columnVisibility[statusColumn.key] !== false;
  const tableColSpan = visibleDataColumns.length + (showStatusColumn ? 1 : 0) + 2;

  const renderCellValue = (r, col) => {
    switch (col.key) {
      case 'ref': return r.ref;
      case 'subject': return notesOnly ? (r.notes?.slice(0, 80) || '—') : r.subject;
      case 'contact': return r.contact;
      case 'phone': return r.phone ? formatCrmPhone(r.phone) : '—';
      case 'type': return typeLabel(r.type);
      case 'meetingType': return p(`meeting_${r.meetingType}`) || typeLabel(r.type);
      case 'date': return formatCrmDate(r.date, i18n.language, { includeTime: true });
      case 'outcome': return outcomeLabel(r.outcome);
      case 'duration': return r.duration ? ps('minutes', { count: r.duration }) : statusLabel(r.status);
      case 'dealAmount': return r.dealAmount != null ? formatCrmMoney(r.dealAmount) : '—';
      case 'nextActionDate': return r.nextActionDate ? formatCrmDate(r.nextActionDate, i18n.language) : '—';
      default: return '—';
    }
  };

  const renderCell = (r, col) => {
    if (col.key === 'phone' && r.phone) {
      return (
        <span className="flex items-center gap-2">
          <a href={getCrmPhoneTelHref(r.phone)} className="text-green-400 hover:underline">{formatCrmPhone(r.phone)}</a>
          {r.type === 'whatsapp' && getCrmWhatsAppHref(r.phone) && (
            <a href={getCrmWhatsAppHref(r.phone)} target="_blank" rel="noopener noreferrer" className="text-emerald-400" title={ps('type_whatsapp')}><MessageCircle size={14} /></a>
          )}
        </span>
      );
    }
    if (col.key === 'type') {
      return <span className="inline-flex items-center gap-1"><TypeIcon type={r.type} /> {typeLabel(r.type)}</span>;
    }
    if (col.key === 'date') {
      return (
        <>
          {formatCrmDate(r.date, i18n.language, { includeTime: true })}
          {isSaudiWeekend(r.date) && (
            <span className="block text-amber-400 text-xs mt-0.5">{p('weekend_warning')}</span>
          )}
        </>
      );
    }
    return renderCellValue(r, col);
  };

  const getColumnFilterKind = (colKey) => {
    if (colKey === 'type' || colKey === 'outcome' || colKey === 'status') return 'select';
    return 'text';
  };

  const getColumnFilterOptions = (colKey) => {
    if (colKey === 'type') return channelFilterOptions;
    if (colKey === 'outcome') return outcomeFilterOptions;
    if (colKey === 'status') return interactionStatusFilterOptions;
    return undefined;
  };

  const columnFilterRules = useMemo(() => {
    const cols = [...visibleDataColumns];
    if (showStatusColumn && statusColumn) cols.push(statusColumn);
    return cols.map((col) => {
      const getValue = (row) => String(renderCellValue(row, col) ?? '');
      if (getColumnFilterKind(col.key) === 'select') {
        return exactColumnRule(col.key, getValue);
      }
      return textColumnRule(col.key, getValue);
    });
  }, [visibleDataColumns, showStatusColumn, statusColumn, notesOnly, i18n.language, p, ps]);

  const filtered = useMemo(
    () => applyColumnFilters(searchFiltered, colFilters, columnFilterRules),
    [searchFiltered, colFilters, columnFilterRules],
  );

  const pagedFiltered = useMemo(
    () => sliceCrmListItems(filtered, crmConfig.itemsPerPage),
    [filtered, crmConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filtered);

  useEffect(() => {
    if (selectedRecords.length > 0) setEditingKey(null);
  }, [selectedRecords, setEditingKey]);

  const openView = (record) => {
    setViewingRecord(record);
    setShowViewModal(true);
  };

  const closeView = () => {
    setShowViewModal(false);
    setViewingRecord(null);
  };

  const closeModal = () => {
    setFormTab('general');
    setShowModal(false);
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm(typeFilter));
    setFormTab('general');
    setShowModal(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setForm({
      contact_id: record.contact_id ? String(record.contact_id) : '',
      interaction_type: record.type || typeFilter || 'call',
      subject: record.subject || '',
      interaction_date: record.date || '',
      duration_minutes: record.duration ?? '',
      outcome: record.outcome || 'neutral',
      status: record.status || 'completed',
      notes: record.notes || '',
      description: record.description || record.notes || '',
      next_action: record.nextAction || '',
      next_action_date: record.nextActionDate || '',
      assigned_to: record.assignedTo || '',
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      await localApiAdapter.delete(`/crm/interactions/${id}`);
      dispatchCrmApiRefresh('/crm/interactions');
      notifyDeleted(showNotify, entityLabel);
      loadRecords();
    } catch {
      notifyError(showNotify, p('delete_error') || ps('save_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRecords.length || !window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/crm/interactions/${id}`)));
      dispatchCrmApiRefresh('/crm/interactions');
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      setSelectedRecords([]);
      loadRecords();
    } catch {
      notifyError(showNotify, p('delete_error') || ps('save_error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject?.trim()) {
      alert(p('alert_subject_required'));
      return;
    }
    try {
      const payload = {
        contact_id: form.contact_id ? parseInt(form.contact_id, 10) : null,
        interaction_type: typeFilter || form.interaction_type || 'call',
        interaction_date: form.interaction_date || null,
        duration_minutes: parseInt(form.duration_minutes, 10) || 0,
        subject: form.subject.trim(),
        description: form.description || form.notes || '',
        outcome: form.outcome || 'neutral',
        next_action: form.next_action || '',
        next_action_date: form.next_action_date || null,
        assigned_to: form.assigned_to || '',
        status: form.status || 'completed',
        notes: form.notes || '',
      };
      if (editing?.id) {
        await localApiAdapter.put(`/crm/interactions/${editing.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/crm/interactions', payload);
        notifyCreated(showNotify, entityLabel);
      }
      dispatchCrmApiRefresh('/crm/interactions');
      closeModal();
      loadRecords();
    } catch {
      notifyError(showNotify, p('save_error') || ps('save_error'));
    }
  };

  const handleExportCsv = () => {
    const exportCols = [...visibleDataColumns];
    if (showStatusColumn && statusColumn) exportCols.push(statusColumn);
    const headers = exportCols.map((col) => ({ key: col.key, label: p(col.labelKey) || col.labelKey }));
    const rows = filtered.map((r) => {
      const row = {};
      exportCols.forEach((col) => { row[col.key] = renderCellValue(r, col); });
      return row;
    });
    exportToCSV(rows, headers, `${pageKey}-${new Date().toISOString().split('T')[0]}.csv`);
    setExportOpen(false);
  };

  const interactionFormTabs = useMemo(() => [
    { id: 'general', label: p('section_interaction_info', { defaultValue: 'معلومات التفاعل' }), icon: Activity },
    { id: 'followup', label: p('section_outcome_followup', { defaultValue: 'النتيجة والمتابعة' }), icon: Clock },
  ], [p]);

  return (
    <>
      <QawaemErpListPageShell
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Activity}
        stats={statCards}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={selectedRecords.length > 0 ? (
          <button
            type="button"
            onClick={handleBulkDelete}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          >
            <Trash2 size={16} />
            <span>{t('common.delete')} ({selectedRecords.length})</span>
          </button>
        ) : null}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder')}
        onCreate={openNew}
        createLabel={p('new_interaction', { defaultValue: p('create') })}
        columns={qawaemColumnDefs}
        visibleColumnCount={visibleDataColumns.length + (showStatusColumn ? 1 : 0)}
        totalColumnCount={dataColumns.length + (statusColumn ? 1 : 0)}
        headerActions={(
          <button type="button" onClick={loadRecords} className="refresh-button" title={p('refresh')}>
            <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
        )}
        importExport={{
          importOpen: false,
          exportOpen,
          onToggleImport: () => {},
          onToggleExport: () => setExportOpen((v) => !v),
          importRef: exportRef,
          exportRef,
          importLabel: p('import_label') || t('common.import', { defaultValue: 'استيراد' }),
          exportLabel: p('export_label') || t('common.export', { defaultValue: 'تصدير' }),
          generatingLabel: t('common.loading'),
          isGeneratingPDF: false,
          onImportCSV: () => {},
          onImportExcel: () => {},
          onExportPDF: () => {},
          onExportExcel: () => {},
          onExportCSV: handleExportCsv,
        }}
      >
        {viewMode === 'table' ? (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {visibleDataColumns.map((col) => (
                    <TableColumnHeaderFilter
                      key={col.key}
                      columnKey={col.key}
                      label={p(col.labelKey) || col.labelKey}
                      kind={getColumnFilterKind(col.key)}
                      value={colFilters[col.key] ?? ''}
                      onChange={(v) => setColFilter(col.key, v)}
                      options={getColumnFilterOptions(col.key)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                      className="px-6 py-4"
                    />
                  ))}
                  {showStatusColumn ? (
                    <TableColumnHeaderFilter
                      columnKey={statusColumn.key}
                      label={p(statusColumn.labelKey) || statusColumn.labelKey}
                      kind="select"
                      value={colFilters[statusColumn.key] ?? ''}
                      onChange={(v) => setColFilter(statusColumn.key, v)}
                      options={interactionStatusFilterOptions}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      align="center"
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr>
                    <td colSpan={tableColSpan} className="p-12 text-center text-white/60">{ps('loading')}</td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} className="p-12 text-center text-white/60">{p('empty_state')}</td>
                  </tr>
                ) : pagedFiltered.map((r) => (
                  <tr key={r.id} className={`hover:bg-white/5 text-white/90 text-sm ${selectedRecords.includes(r.id) ? 'bg-white/10' : ''}`}>
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(r.id)}
                      onChange={() => handleSelectRecord(r.id)}
                    />
                    {visibleDataColumns.map((col) => (
                      <td key={col.key} className={`px-6 py-4 ${col.key === 'ref' ? 'font-mono text-xs' : ''} ${col.key === 'dealAmount' ? 'font-semibold' : ''}`}>
                        {renderCell(r, col)}
                      </td>
                    ))}
                    {showStatusColumn ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={r.status}
                            recordId={r.id}
                            options={interactionStatusOptions}
                            onChange={handleStatusChange}
                            title={ps('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onView={() => openView(r)}
                        onEdit={() => openEdit(r)}
                        onDelete={() => handleDelete(r.id)}
                        viewTitle={ps('view_details') || t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : loading ? (
          <div className="p-12 text-center text-white/60">{ps('loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-white/60">{p('empty_state')}</div>
        ) : (
          <div className="p-6 grid gap-4 md:grid-cols-3">
            {pagedFiltered.map((r) => (
              <div key={r.id} className="bg-white/[0.03] border border-white/10 rounded-xl p-4 h-full min-w-0">
                <div className="flex justify-between items-start mb-2">
                  <span className="font-semibold text-white">{notesOnly ? r.contact : r.subject}</span>
                  <TypeIcon type={r.type} className="text-blue-400" />
                </div>
                <p className="text-white/60 text-sm mb-2">{notesOnly ? r.notes : r.contact}</p>
                <p className="text-white/50 text-xs">{formatCrmDate(r.date, i18n.language, { includeTime: true })}</p>
                <div className="mt-2">
                  <QawaemClickableStatusBadge
                    status={r.status}
                    recordId={r.id}
                    options={interactionStatusOptions}
                    onChange={handleStatusChange}
                  />
                </div>
                {r.dealAmount != null && <p className="text-emerald-400 font-bold mt-2">{formatCrmMoney(r.dealAmount)}</p>}
                <QawaemErpRowActions
                  compact
                  size={14}
                  className="mt-3 justify-start"
                  onView={() => openView(r)}
                  onEdit={() => openEdit(r)}
                  onDelete={() => handleDelete(r.id)}
                  viewTitle={ps('view_details') || t('common.view')}
                  editTitle={t('common.edit')}
                  deleteTitle={t('common.delete')}
                />
              </div>
            ))}
          </div>
        )}
      </QawaemErpListPageShell>

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={closeModal}
          title={editing ? p('modal_edit') : p('modal_new')}
          icon={Activity}
          formId={FORM_ID}
          saveLabel={t('common.save')}
          onSubmit
          maxWidth="1200px"
          testId="crm-interactions-modal"
        >
          <CrmModalFormBody>
            <CrmModalFormTabs
              tabs={interactionFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              ariaLabel={editing ? p('modal_edit') : p('modal_new')}
            />
            <CrmModalFormPanel>
              <form id={FORM_ID} onSubmit={handleSubmit}>
                {formTab === 'general' && (
                  <CrmModalFieldGrid>
                    <div>
                      <label className={cfgTheme.label}>{p('col_contact')}</label>
                      <CustomSelect
                        value={form.contact_id}
                        onChange={(v) => setForm({ ...form, contact_id: v })}
                        options={contactOptions}
                        placeholder={p('select', { defaultValue: ps('filter_all') })}
                      />
                    </div>
                    {!typeFilter ? (
                      <div>
                        <label className={cfgTheme.label}>{p('label_interaction_type')}</label>
                        <CustomSelect
                          value={form.interaction_type}
                          onChange={(v) => setForm({ ...form, interaction_type: v })}
                          options={typeFormOptions}
                        />
                      </div>
                    ) : null}
                    <div>
                      <label className={cfgTheme.label}>{p('label_subject')}<span className="text-red-600 font-bold"> *</span></label>
                      <input
                        required
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_subject')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_datetime')}</label>
                      <CustomDatePicker
                        value={form.interaction_date}
                        onChange={(v) => setForm({ ...form, interaction_date: v })}
                        showTime
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_duration_minutes')}</label>
                      <input
                        type="number"
                        min="0"
                        value={form.duration_minutes}
                        onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_duration_example')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_assigned_to')}</label>
                      <CustomSelect
                        value={form.assigned_to}
                        onChange={(v) => setForm({ ...form, assigned_to: v })}
                        options={agentOptions}
                        placeholder={p('placeholder_agent_name')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{ps('view_status')}</label>
                      <CustomSelect
                        value={form.status}
                        onChange={(v) => setForm({ ...form, status: v })}
                        options={interactionStatusOptions.map((s) => ({ value: s.value, label: s.label }))}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className={cfgTheme.label}>{p('label_description')}</label>
                      <textarea
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        rows={3}
                        className={`${cfgTheme.input} resize-none`}
                        placeholder={p('placeholder_description')}
                      />
                    </div>
                  </CrmModalFieldGrid>
                )}
                {formTab === 'followup' && (
                  <CrmModalFieldGrid>
                    <div>
                      <label className={cfgTheme.label}>{p('label_outcome')}</label>
                      <CustomSelect
                        value={form.outcome}
                        onChange={(v) => setForm({ ...form, outcome: v })}
                        options={outcomeFilterOptions}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_next_action')}</label>
                      <input
                        value={form.next_action}
                        onChange={(e) => setForm({ ...form, next_action: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_next_action')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_next_action_date')}</label>
                      <CustomDatePicker
                        value={form.next_action_date}
                        onChange={(v) => setForm({ ...form, next_action_date: v })}
                        showTime={false}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className={cfgTheme.label}>{p('label_notes')}</label>
                      <textarea
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={3}
                        className={`${cfgTheme.input} resize-none`}
                        placeholder={p('placeholder_notes_long')}
                      />
                    </div>
                  </CrmModalFieldGrid>
                )}
              </form>
            </CrmModalFormPanel>
          </CrmModalFormBody>
        </QawaemErpCrudModal>
      )}

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={closeView}
          title={p('view_title', { defaultValue: ps('view_details') })}
          icon={Activity}
          maxWidth="640px"
          testId="crm-interactions-view-modal"
        >
          <div className={`space-y-3 text-sm ${cfgTheme.light ? 'text-slate-700' : 'text-white/80'}`} dir="rtl">
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_ref')}:</span> {viewingRecord.ref}</p>
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_subject')}:</span> {viewingRecord.subject}</p>
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_contact')}:</span> {viewingRecord.contact}</p>
            {viewingRecord.phone && (
              <p className="flex items-center gap-2 flex-wrap">
                <span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_phone')}:</span>
                <a href={getCrmPhoneTelHref(viewingRecord.phone)} className="text-green-400">{formatCrmPhone(viewingRecord.phone)}</a>
                {getCrmWhatsAppHref(viewingRecord.phone) && (
                  <a href={getCrmWhatsAppHref(viewingRecord.phone)} target="_blank" rel="noopener noreferrer" className="text-emerald-400 text-xs">{ps('view_whatsapp')}</a>
                )}
              </p>
            )}
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_date')}:</span> {formatCrmDate(viewingRecord.date, i18n.language, { includeTime: true })}</p>
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_channel')}:</span> {typeLabel(viewingRecord.type)}</p>
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_status')}:</span> {statusLabel(viewingRecord.status)}</p>
            {viewingRecord.dealAmount != null && <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_deal_amount')}:</span> <strong className="text-emerald-400">{formatCrmMoney(viewingRecord.dealAmount)}</strong></p>}
            {viewingRecord.notes && <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_notes')}:</span> {viewingRecord.notes}</p>}
            {viewingRecord.nextAction && <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_next_action')}:</span> {viewingRecord.nextAction} — {formatCrmDate(viewingRecord.nextActionDate, i18n.language)}</p>}
          </div>
        </QawaemErpViewModal>
      ) : null}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
}
